import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowRight,
  MapPin,
  Clock,
  Briefcase,
  GraduationCap,
  CheckCircle,
  Send,
  Loader2,
  Globe,
  Users,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const internshipPositions = [
  {
    id: 1,
    title: 'Sustainability Data Analyst Intern',
    location: 'Singapore',
    type: 'Internship',
    duration: '3-6 months',
    department: 'Data & Analytics',
    description: 'Join our data team to help analyze and process sustainability metrics for enterprise clients. You will work with real ESG data and contribute to building audit-ready reports.',
    responsibilities: [
      'Assist in collecting, cleaning, and validating sustainability data from various sources',
      'Support the development of ESG dashboards and visualizations',
      'Help prepare sustainability baseline assessments for clients',
      'Conduct research on industry-specific sustainability metrics and benchmarks',
      'Collaborate with senior analysts on client deliverables',
    ],
    requirements: [
      'Currently pursuing a degree in Environmental Science, Data Science, Business Analytics, or related field',
      'Strong analytical and problem-solving skills',
      'Proficiency in Excel; experience with Python or SQL is a plus',
      'Interest in sustainability, ESG, and corporate responsibility',
      'Excellent communication skills in English',
    ],
    benefits: [
      'Hands-on experience with real enterprise sustainability projects',
      'Mentorship from experienced sustainability professionals',
      'Exposure to cutting-edge ESG data platforms',
      'Potential for full-time conversion',
      'Competitive internship allowance',
    ],
  },
  {
    id: 2,
    title: 'Software Engineering Intern',
    location: 'Singapore',
    type: 'Internship',
    duration: '3-6 months',
    department: 'Engineering',
    description: 'Work with our engineering team to build and enhance our AI-powered Sustainability Data Platform. Gain experience in full-stack development with modern technologies.',
    responsibilities: [
      'Develop and maintain features for our sustainability data platform',
      'Write clean, maintainable code following best practices',
      'Participate in code reviews and technical discussions',
      'Assist in building APIs and integrations with external data sources',
      'Contribute to documentation and testing',
    ],
    requirements: [
      'Currently pursuing a degree in Computer Science, Software Engineering, or related field',
      'Experience with Python, JavaScript/TypeScript, or similar languages',
      'Familiarity with web frameworks (React, FastAPI, Django) is a plus',
      'Understanding of databases and API development',
      'Strong problem-solving skills and attention to detail',
    ],
    benefits: [
      'Work on production systems used by enterprise clients',
      'Learn from experienced engineers in a startup environment',
      'Exposure to AI/ML applications in sustainability',
      'Flexible working arrangements',
      'Competitive internship allowance',
    ],
  },
  {
    id: 3,
    title: 'Sustainability Consulting Intern',
    location: 'Philippines',
    type: 'Internship',
    duration: '3-6 months',
    department: 'Consulting',
    description: 'Support our consulting team in delivering sustainability transformation projects for IT-BPM and other industries in the Philippines. Gain exposure to ESG frameworks and client engagement.',
    responsibilities: [
      'Assist in conducting materiality assessments for client organizations',
      'Support the preparation of sustainability reports aligned with global frameworks',
      'Help gather and organize ESG data from client facilities',
      'Conduct industry research on sustainability trends and best practices',
      'Participate in client meetings and workshops',
    ],
    requirements: [
      'Currently pursuing a degree in Environmental Management, Business, or related field',
      'Interest in corporate sustainability, ESG, and the IT-BPM industry',
      'Strong research and analytical skills',
      'Excellent written and verbal communication skills',
      'Proficiency in Microsoft Office; knowledge of sustainability frameworks is a plus',
    ],
    benefits: [
      'Direct client exposure and professional development',
      'Learn industry-standard sustainability reporting frameworks (GRI, SASB, TCFD)',
      'Work on high-profile industry association projects',
      'Networking opportunities within the IT-BPM ecosystem',
      'Competitive internship allowance',
    ],
  },
  {
    id: 4,
    title: 'Marketing & Communications Intern',
    location: 'Philippines',
    type: 'Internship',
    duration: '3-6 months',
    department: 'Marketing',
    description: 'Join our marketing team to help communicate our sustainability mission and build awareness of our solutions in the Asia-Pacific market.',
    responsibilities: [
      'Assist in creating content for social media, blog posts, and marketing materials',
      'Support the planning and execution of webinars and events',
      'Help manage our LinkedIn presence and engagement',
      'Conduct market research on sustainability trends and competitors',
      'Assist in preparing case studies and client success stories',
    ],
    requirements: [
      'Currently pursuing a degree in Marketing, Communications, or related field',
      'Strong writing and content creation skills',
      'Familiarity with social media platforms, especially LinkedIn',
      'Interest in sustainability and B2B technology marketing',
      'Creative mindset with attention to detail',
    ],
    benefits: [
      'Build your portfolio with real marketing campaigns',
      'Learn B2B marketing strategies in the sustainability space',
      'Flexible remote working options',
      'Exposure to regional marketing initiatives',
      'Competitive internship allowance',
    ],
  },
];

const whyJoin = [
  {
    icon: Globe,
    title: 'Make an Impact',
    description: 'Work on projects that drive real sustainability outcomes for organizations across Asia.',
  },
  {
    icon: GraduationCap,
    title: 'Learn & Grow',
    description: 'Gain hands-on experience with cutting-edge sustainability technologies and frameworks.',
  },
  {
    icon: Users,
    title: 'Collaborative Culture',
    description: 'Join a diverse, supportive team passionate about sustainability and innovation.',
  },
  {
    icon: Sparkles,
    title: 'Career Development',
    description: 'High-performing interns have opportunities for full-time roles upon graduation.',
  },
];

export const Careers = () => {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    linkedinUrl: '',
    coverLetter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = (position) => {
    setSelectedPosition(position);
    setFormData((prev) => ({ ...prev, position: position.title }));
    // Scroll to application form
    document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/career-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          position: formData.position,
          linkedin_url: formData.linkedinUrl || null,
          cover_letter: formData.coverLetter,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Application submission error:', error);
      setIsSubmitting(false);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-6 border-teal/30 text-teal bg-teal/10">
              Careers at SusTek
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Join Our Mission to Transform{' '}
              <span className="text-teal">Sustainability Data</span>
            </h1>
            <p className="text-xl text-white/70 mb-8">
              We are looking for passionate individuals to help build the credibility
              infrastructure for sustainability finance. Explore our internship opportunities
              in Singapore and the Philippines.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Why SusTek</Badge>
            <h2 className="text-3xl font-bold text-foreground">
              Why Join Us
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyJoin.map((item, index) => (
              <Card key={index} className="text-center border-border/50 bg-card hover-lift">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-teal/10 text-teal flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Open Positions</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Internship Opportunities
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We offer internships in Singapore and the Philippines. Join us to gain
              hands-on experience in sustainability data and consulting.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {internshipPositions.map((position) => (
              <Card key={position.id} className="border-border/50 bg-card hover-lift">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl mb-2">{position.title}</CardTitle>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary" className="bg-teal/10 text-teal">
                          <MapPin className="h-3 w-3 mr-1" />
                          {position.location}
                        </Badge>
                        <Badge variant="outline">
                          <Clock className="h-3 w-3 mr-1" />
                          {position.duration}
                        </Badge>
                        <Badge variant="outline">
                          <Briefcase className="h-3 w-3 mr-1" />
                          {position.department}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base mb-4">
                    {position.description}
                  </CardDescription>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Key Responsibilities</h4>
                      <ul className="space-y-1">
                        {position.responsibilities.slice(0, 3).map((item, idx) => (
                          <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Requirements</h4>
                      <ul className="space-y-1">
                        {position.requirements.slice(0, 3).map((item, idx) => (
                          <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <Button
                    variant="teal"
                    className="w-full mt-6 group"
                    onClick={() => handleApply(position)}
                  >
                    Apply Now
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Apply Now</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {selectedPosition ? `Apply for ${selectedPosition.title}` : 'Submit Your Application'}
            </h2>
            <p className="text-lg text-muted-foreground">
              Interested in joining SusTek? Fill out the form below to apply.
            </p>
          </div>

          <Card className="border-border/50 bg-card">
            <CardContent className="pt-6">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-success" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Application Submitted!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for your interest in joining SusTek. We will review your
                    application and get back to you soon.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsSubmitted(false);
                      setSelectedPosition(null);
                      setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        position: '',
                        linkedinUrl: '',
                        coverLetter: '',
                      });
                    }}
                  >
                    Submit Another Application
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        placeholder="+65 9123 4567"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="position">Position *</Label>
                      <Select
                        value={formData.position}
                        onValueChange={(value) => handleChange('position', value)}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select position" />
                        </SelectTrigger>
                        <SelectContent>
                          {internshipPositions.map((pos) => (
                            <SelectItem key={pos.id} value={pos.title}>
                              {pos.title} ({pos.location})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="linkedinUrl">LinkedIn Profile URL</Label>
                    <Input
                      id="linkedinUrl"
                      placeholder="https://linkedin.com/in/yourprofile"
                      value={formData.linkedinUrl}
                      onChange={(e) => handleChange('linkedinUrl', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="coverLetter">Cover Letter / Why do you want to join SusTek? *</Label>
                    <Textarea
                      id="coverLetter"
                      placeholder="Tell us about yourself, your interest in sustainability, and why you want to join SusTek..."
                      rows={6}
                      value={formData.coverLetter}
                      onChange={(e) => handleChange('coverLetter', e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="teal"
                    size="lg"
                    className="w-full group"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Careers;
